import { Pencil } from "lucide-react";
import { ActionIcon, Drawer, LoadingOverlay } from "@mantine/core";
import React, { useEffect, useState } from "react";
import AnnouncementForm from "../components/AnnouncementForm";
import useAnnouncementDetailsApiService from "../services/api/AnnouncementDetailsApiService";
import { HttpClientError } from "../../../providers/HttpClient";
import { queryClient } from "../../../App";
import { useToasts } from "../../../providers/notifications/ToastsContext";

type UpdateAnnouncementActionIconProps = {
  id: number;
};

const UpdateAnnouncementActionIcon: React.FC<
  UpdateAnnouncementActionIconProps
> = ({ id }) => {
  const {
    isLoading,
    isError,
    error,
    refetch,
    isRefetchError,
    isSuccess,
    data,
  } = useAnnouncementDetailsApiService(id, false);
  const { showToast } = useToasts();
  const [opened, setOpened] = useState(false);
  const [isFetchingFreshData, setIsFetchingFreshData] = useState(false);

  useEffect(() => {
    if (isError || isRefetchError) {
      showToast({
        strapiId: "announcement_refetch_error",
        data: {
          message: HttpClientError(error),
        },
      });

      setOpened(false);
      setIsFetchingFreshData(false);
    }
  }, [isError, isRefetchError, error]);

  const handleEditClick = async (): Promise<void> => {
    try {
      setIsFetchingFreshData(true);

      // Invalidate cache and fetch fresh data before opening drawer
      await queryClient.invalidateQueries([
        "AnnouncementDetailsApiService",
        id,
      ]);
      const result = await refetch();

      // Only open drawer if fresh data was successfully loaded
      if (result.isSuccess) {
        setOpened(true);
      }
    } finally {
      setIsFetchingFreshData(false);
    }
  };

  return (
    <>
      <LoadingOverlay visible={isFetchingFreshData || isLoading} />
      <Drawer
        opened={isSuccess && opened && !isFetchingFreshData}
        size="xl"
        onClose={() => setOpened(false)}
        title="Edit Announcement"
        overlayOpacity={0.1}
        overlayBlur={3}
        position="right"
      >
        <div style={{ padding: "25px", height: "100%", overflow: "auto" }}>
          <AnnouncementForm values={data} onSuccess={() => setOpened(false)} />
        </div>
      </Drawer>

      <ActionIcon
        onClick={handleEditClick}
        disabled={isFetchingFreshData || isLoading}
      >
        <Pencil size={18} />
      </ActionIcon>
    </>
  );
};

export default UpdateAnnouncementActionIcon;
